import { addDays, addWeeks, endOfWeek, format, isSameDay, startOfWeek } from 'date-fns'
import { enGB, nb } from 'date-fns/locale'
import React from 'react'
import { Navigate } from 'react-big-calendar'
import { ICalendarEvent } from 'src/generated-types/calendarEvent'
import i18n from '../../i18n'
import { useCalendarTypeStyle } from './calendarColors'
import { EventHover, EventSegments } from './CalendarEventContent'
import { getEventTextMaxWidth, useAvailableWidth } from './calendarLayout'
import {
  DayColumn,
  DayEventList,
  DayHeader,
  DayName,
  DayNumber,
  EmptyDay,
  WeekEventCardBox,
  WeekGrid,
} from './CalendarWeekView.style'

const WEEK_OPTS = { weekStartsOn: 1 as const } // Monday-first (Norwegian convention)

const dfnsLocale = () => (i18n.language?.toLowerCase().startsWith('en') ? enGB : nb)

const weekDays = (date: Date): Date[] => {
  const start = startOfWeek(date, WEEK_OPTS)
  return Array.from({ length: 7 }, (_, i) => addDays(start, i))
}

// An event belongs to a given day if that day falls within its [start, end] span (date granularity).
const eventOnDay = (event: ICalendarEvent, day: Date): boolean => {
  const start = new Date(event.start)
  const end = event.end ? new Date(event.end) : start
  const dayStart = new Date(day.getFullYear(), day.getMonth(), day.getDate())
  const startDay = new Date(start.getFullYear(), start.getMonth(), start.getDate())
  const endDay = new Date(end.getFullYear(), end.getMonth(), end.getDate())
  return dayStart >= startDay && dayStart <= endDay
}

interface CalendarWeekViewProps {
  date: Date
  events: ICalendarEvent[]
  onSelectEvent?: (event: ICalendarEvent, e: React.SyntheticEvent) => void
}

const WeekEventCard = ({
  event,
  onSelectEvent,
  maxTextWidth,
}: {
  event: ICalendarEvent
  onSelectEvent?: (event: ICalendarEvent, e: React.SyntheticEvent) => void
  maxTextWidth?: number
}) => {
  const getTypeStyle = useCalendarTypeStyle()
  const { tint, text, accent } = getTypeStyle(event.type)
  return (
    <EventHover event={event}>
      <WeekEventCardBox
        onClick={e => onSelectEvent?.(event, e)}
        tint={tint}
        text={text}
        accent={accent}
        isProjected={!!event.isProjected}
      >
        <EventSegments event={event} maxTextWidth={maxTextWidth} />
      </WeekEventCardBox>
    </EventHover>
  )
}

// Custom react-big-calendar view: seven day columns, each a scrollable list of event rows
// (AO-nr | Lokalitet | Ressurs | Tittel for work orders). Registered via the Calendar `views` map,
// so it reuses the standard toolbar navigation.
const CalendarWeekView = ({ date, events, onSelectEvent }: CalendarWeekViewProps) => {
  const days = weekDays(date)
  const locale = dfnsLocale()
  const today = new Date()

  const [gridRef, gridWidth] = useAvailableWidth<HTMLDivElement>()
  const eventTextMaxWidth = gridWidth ? getEventTextMaxWidth(gridWidth) : undefined

  return (
    <WeekGrid ref={gridRef}>
      {days.map(day => {
        const dayEvents = events.filter(e => eventOnDay(e, day))
        const isToday = isSameDay(day, today)
        return (
          <DayColumn key={day.toISOString()}>
            <DayHeader isToday={isToday}>
              <DayName variant="caption">{format(day, 'EEE', { locale })}</DayName>
              <DayNumber isToday={isToday}>{format(day, 'd')}</DayNumber>
            </DayHeader>
            <DayEventList spacing={0.75}>
              {dayEvents.length === 0 ? (
                <EmptyDay variant="caption">–</EmptyDay>
              ) : (
                dayEvents.map(event => (
                  <WeekEventCard
                    key={event.id}
                    event={event}
                    onSelectEvent={onSelectEvent}
                    maxTextWidth={eventTextMaxWidth}
                  />
                ))
              )}
            </DayEventList>
          </DayColumn>
        )
      })}
    </WeekGrid>
  )
}

// --- Static metadata react-big-calendar requires for a custom view ---

CalendarWeekView.range = (date: Date): Date[] => weekDays(date)

CalendarWeekView.navigate = (date: Date, action: string): Date => {
  switch (action) {
    case Navigate.PREVIOUS:
      return addWeeks(date, -1)
    case Navigate.NEXT:
      return addWeeks(date, 1)
    default:
      return date
  }
}

CalendarWeekView.title = (date: Date): string => {
  const start = startOfWeek(date, WEEK_OPTS)
  const end = endOfWeek(date, WEEK_OPTS)
  const locale = dfnsLocale()
  return `${format(start, 'd. MMM', { locale })} – ${format(end, 'd. MMM yyyy', { locale })}`
}

export default CalendarWeekView
